import Vue from "vue";

const NEWPLAYER = {
  name: "",
  id: "",
  avatar: "",
  // 共玩成员是公开座位状态。空座位也预先带上这个键，避免运行期
  // “新增属性”导致 Vue 2 侦测不到、界面不重绘。
  coPlayMembers: [],
  role: {},
  // A character's printed team and a player's current alignment can differ.
  alignment: "",
  reminders: [],
  // A seat normally contributes one vote. The storyteller can adjust this
  // for script effects that grant or remove additional votes.
  votes: 1,
  isVoteless: false,
  isDead: false,
  pronouns: ""
};

const state = () => ({
  players: [],
  fabled: [],
  bluffs: []
});

const getters = {
  alive({ players }) {
    return players.filter(player => !player.isDead).length;
  },
  nonTravelers({ players }) {
    const nonTravelers = players.filter(
      player => player.role.team !== "traveler"
    );
    return Math.min(nonTravelers.length, 15);
  },
  // calculate a Map of player => night order
  nightOrder({ players, fabled }) {
    const firstNight = [0];
    const otherNight = [0];
    players.forEach(({ role }) => {
      if (role.firstNight && !firstNight.includes(role.firstNight)) {
        firstNight.push(role.firstNight);
      }
      if (role.otherNight && !otherNight.includes(role.otherNight)) {
        otherNight.push(role.otherNight);
      }
    });
    fabled.forEach(role => {
      if (role.firstNight && !firstNight.includes(role.firstNight)) {
        firstNight.push(role.firstNight);
      }
      if (role.otherNight && !otherNight.includes(role.otherNight)) {
        otherNight.push(role.otherNight);
      }
    });
    firstNight.sort((a, b) => a - b);
    otherNight.sort((a, b) => a - b);
    const nightOrder = new Map();
    players.forEach(player => {
      const first = Math.max(firstNight.indexOf(player.role.firstNight), 0);
      const other = Math.max(otherNight.indexOf(player.role.otherNight), 0);
      nightOrder.set(player, { first, other });
    });
    fabled.forEach(role => {
      const first = Math.max(firstNight.indexOf(role.firstNight), 0);
      const other = Math.max(otherNight.indexOf(role.otherNight), 0);
      nightOrder.set(role, { first, other });
    });
    return nightOrder;
  }
};

const actions = {
  // 旧的本页洗牌：只改本地顺序，服务器并不知道，刷新就会回滚。
  // 说书人「随机座位」必须走 players/reorder（服务器权威整体重排），
  // 这个 action 只为兼容保留，不要再接任何真实功能入口。
  randomize({ state, commit }) {
    const players = state.players
      .map(a => [Math.random(), a])
      .sort((a, b) => a[0] - b[0])
      .map(a => a[1]);
    commit("set", players);
  },
  clearRoles({ state, commit, rootState }) {
    let players;
    if (rootState.session.isSpectator) {
      players = state.players.map(player => {
        if (player.role.team !== "traveler") {
          player.role = {};
        }
        player.reminders = [];
        return player;
      });
    } else {
      players = state.players.map(({ name, id, avatar, pronouns }) => ({
        ...NEWPLAYER,
        name,
        id,
        avatar,
        pronouns
      }));
      commit("setFabled", { fabled: [] });
    }
    commit("set", players);
    commit("setBluff");
  }
};

const mutations = {
  clear(state) {
    state.players = [];
    state.bluffs = [];
    state.fabled = [];
  },
  set(state, players = []) {
    state.players = players;
  },
  /**
  The update mutation also has a property for isFromSockets
  this property can be addded to payload object for any mutations
  then can be used to prevent infinite loops when a property is
  able to be set from multiple different session on websockets.
  An example of this is in the sendPlayerPronouns and _updatePlayerPronouns
  in socket.js.
   */
  update(state, { player, property, value }) {
    const index = state.players.indexOf(player);
    if (index >= 0) {
      const target = state.players[index];
      if (Object.prototype.hasOwnProperty.call(target, property)) {
        target[property] = value;
      } else {
        // Vue 2 侦测不到“新增属性”的普通赋值：状态其实变了，界面却永远
        // 不重绘（共玩标识与合并 ID 只在刷新后才出现就是这个原因）。
        Vue.set(target, property, value);
      }
    }
  },
  // A storyteller identity change has two inseparable fields. Replacing the
  // seat object in one mutation makes Vue re-evaluate the token, its team
  // class, and its alignment ring together instead of leaving an interim
  // role/alignment frame behind.
  setIdentity(state, { player, role, alignment } = {}) {
    const index = state.players.indexOf(player);
    if (index < 0) return;
    state.players.splice(index, 1, {
      ...state.players[index],
      role: role || {},
      // Empty/unknown identity is a real third state. Treating every
      // non-evil value as good left brand-new vacant seats with a blue ring
      // after a private archive restore.
      alignment: alignment === "evil" ? "evil" : alignment === "good" ? "good" : ""
    });
  },
  add(state, name) {
    state.players.push({
      ...NEWPLAYER,
      name
    });
  },
  // 批量增加空座位只触发一次 store 订阅与一次联机同步。
  // 逐个 commit 会把“增加 10 个座位”放大成大量完整房间状态消息。
  addSeats(state, amount = 1) {
    const count = Math.min(
      Math.max(0, Math.floor(Number(amount) || 0)),
      Math.max(0, 20 - state.players.length)
    );
    for (let index = 0; index < count; index += 1) {
      state.players.push({
        ...NEWPLAYER,
        name: "空"
      });
    }
  },
  remove(state, index) {
    state.players.splice(index, 1);
  },
  /**
   * 开始新一局：只清“上一局”的局内状态。
   * 座位数量、落座玩家、玩家 ID/头像/昵称、共玩关系、旅行者身份等持久
   * 字段全部保留；角色、阵营、提示标记、票数、死亡与魂票回到初始值。
   * 恶魔伪装角色属于本局配置，因此一并清空。
   */
  clearRoundState(state) {
    state.players = state.players.map(player => ({
      ...player,
      role: {},
      alignment: "",
      reminders: [],
      votes: 1,
      isVoteless: false,
      isDead: false
    }));
    state.bluffs = [];
    // 传奇角色（Fabled）属于本局配置，新一局不保留。
    state.fabled = [];
  },
  // Every online view must drop the previous game's private role face as soon
  // as the server announces gameReset. Public seat/profile data remains.
  clearIdentityState(state) {
    state.players = state.players.map(player => ({
      ...player,
      role: {},
      alignment: ""
    }));
  },
  clearStorytellerPrivateView(state, { keepPublishedReview = false } = {}) {
    state.players = state.players.map(player => ({
      ...player,
      role: keepPublishedReview ? player.role : {},
      alignment: keepPublishedReview ? player.alignment : "",
      reminders: keepPublishedReview ? player.reminders : []
    }));
    state.bluffs = [];
  },
  swap(state, [from, to]) {
    [state.players[from], state.players[to]] = [
      state.players[to],
      state.players[from]
    ];
    // hack: "modify" the array so that Vue notices something changed
    state.players.splice(0, 0);
  },
  move(state, [from, to]) {
    state.players.splice(to, 0, state.players.splice(from, 1)[0]);
  },
  /**
   * 说书人「随机座位」：整体重排（order[新下标] = 旧下标）。
   *
   * 这是服务器权威重排的本地一半：调用方必须把同一个 permutation 提交到
   * socket 链（players/reorder → session.reorderSeats → 服务端），否则就又
   * 退化成「只有本页视觉洗牌」。用原地 splice 保持数组引用不变，避免与
   * 其它按引用持有 state.players 的逻辑产生分叉。
   */
  reorder(state, order) {
    const players = state.players;
    if (!Array.isArray(order) || order.length !== players.length) return;
    const seen = new Set();
    for (const source of order) {
      if (
        !Number.isInteger(source) ||
        source < 0 ||
        source >= players.length ||
        seen.has(source)
      ) return;
      seen.add(source);
    }
    // 必须整表换成新数组：原地 splice 重排同一批对象引用时，Vue 2 的 keyed
    // v-for 会漏掉一个座位节点（实测 DOM 只剩 4 个 li、有人从圆桌上消失）。
    // players/set 用的也是「换数组」这条路径，渲染结果才是正确的。
    state.players = order.map(source => players[source]);
  },
  setBluff(state, { index, role } = {}) {
    if (index !== undefined) {
      state.bluffs.splice(index, 1, role);
    } else {
      state.bluffs = [];
    }
  },
  setFabled(state, { index, fabled } = {}) {
    if (index !== undefined) {
      state.fabled.splice(index, 1);
    } else if (fabled) {
      if (!Array.isArray(fabled)) {
        state.fabled.push(fabled);
      } else {
        state.fabled = fabled;
      }
    }
  },
  applyBootlegger(state, content = "") {
    const bootlegger = state.fabled.find(role => role && role.id === "bootlegger");
    if (!bootlegger) return;
    if (!bootlegger.defaultAbility) bootlegger.defaultAbility = bootlegger.ability;
    bootlegger.ability = content || bootlegger.defaultAbility;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
